import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BranchProvider } from "./context/BranchContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Branches from "./pages/Branches";
import Staff from "./pages/Staff";
import Services from "./pages/Services";
import Customers from "./pages/Customers";
import Appointments from "./pages/Appointments";
import Billing from "./pages/Billing";
import BillingPOS from "./pages/BillingPOS";
import Attendance from "./pages/Attendance";
import Salary from "./pages/Salary";
import Incentives from "./pages/Incentives";
import Expenses from "./pages/Expenses";
import Reports from "./pages/Reports";
import FreedomPackageReport from "./pages/FreedomPackageReport";
import StaffServicePerformanceReport from "./pages/StaffServicePerformanceReport";
import ServiceRevenueReport from "./pages/ServiceRevenueReport";
import NewClientRepeatReport from "./pages/NewClientRepeatReport";
import NewVsExistingReport from "./pages/NewVsExistingReport";
import RetentionReport from "./pages/RetentionReport";
import ExpenseReport from "./pages/ExpenseReport";
import AuditLog from "./pages/AuditLog";
import Settings from "./pages/Settings";
import Packages from "./pages/Packages";
import Products from "./pages/Products";
import WhatsAppSettings from "./pages/WhatsAppSettings";
import BookingSite from "./pages/BookingSite";
import WebsiteImages from "./pages/WebsiteImages";
import WhatsAppAI from "./pages/WhatsAppAI";
import AIAssistant from "./pages/AIAssistant";
import PublicBooking from "./pages/PublicBooking";
import { Branding } from "./components/Branding";
import RateVisit from "./pages/RateVisit";
import OffersManager from "./pages/OffersManager";
import PublicInvoice from "./pages/PublicInvoice";
import Inventory from "./pages/Inventory";
import ManagerAccounts from "./pages/ManagerAccounts";
import ServiceMenu from "./pages/ServiceMenu";
import Enquiries from "./pages/Enquiries";
import Campaigns from "./pages/Campaigns";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const ADMIN = ["OWNER", "SUPER_ADMIN"];
// Mount-time authorization guard: redirects to dashboard if the user lacks the
// permission (or admin role), independent of any API call.
function Perm({ perm, anyPerm, adminOnly, denyRoles, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (denyRoles && denyRoles.includes(user.role)) return <Navigate to="/" replace />;
  if (ADMIN.includes(user.role)) return children;
  if (adminOnly) return <Navigate to="/" replace />;
  if (perm && !(user.permissions || {})[perm]) return <Navigate to="/" replace />;
  if (anyPerm && !anyPerm.some((p) => (user.permissions || {})[p])) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Branding />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/book" element={<PublicBooking />} />
            <Route path="/rate" element={<RateVisit />} />
            <Route path="/invoice/:id" element={<PublicInvoice />} />
            <Route
              path="/"
              element={
                <Protected>
                  <BranchProvider>
                    <Layout />
                  </BranchProvider>
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="appointments" element={<Perm perm="appointment_view"><Appointments /></Perm>} />
              <Route path="billing" element={<Perm perm="billing_view"><Billing /></Perm>} />
              <Route path="billing/new" element={<Perm perm="billing_create"><BillingPOS /></Perm>} />
              <Route path="customers" element={<Perm perm="customer_view"><Customers /></Perm>} />
              <Route path="staff" element={<Perm anyPerm={["staff_view", "add_staff", "edit_staff"]} denyRoles={["RECEPTION"]}><Staff /></Perm>} />
              <Route path="attendance" element={<Perm perm="attendance_view"><Attendance /></Perm>} />
              <Route path="salary" element={<Perm perm="salary_view"><Salary /></Perm>} />
              <Route path="incentives" element={<Perm perm="incentive_view"><Incentives /></Perm>} />
              <Route path="services" element={<Perm perm="services_view"><Services /></Perm>} />
              <Route path="offers" element={<Perm adminOnly><OffersManager /></Perm>} />
              <Route path="products" element={<Perm perm="products_view"><Products /></Perm>} />
              <Route path="inventory" element={<Perm perm="inventory_view"><Inventory /></Perm>} />
              <Route path="whatsapp" element={<Perm anyPerm={["whatsapp_access", "whatsapp_chat_view", "whatsapp_chat_send"]}><WhatsAppSettings /></Perm>} />
              <Route path="whatsapp-ai" element={<Perm perm="whatsapp_access"><WhatsAppAI /></Perm>} />
              <Route path="campaigns" element={<Perm anyPerm={["campaign_view", "campaign_create", "campaign_send", "campaign_schedule", "campaign_reports", "send_whatsapp_campaign"]}><Campaigns /></Perm>} />
              <Route path="packages" element={<Perm perm="packages_view"><Packages /></Perm>} />
              <Route path="branches" element={<Perm adminOnly><Branches /></Perm>} />
              <Route path="expenses" element={<Perm perm="expenses_view"><Expenses /></Perm>} />
              <Route path="reports" element={<Perm anyPerm={["reports_view", "expense_report"]}><Reports /></Perm>} />
              <Route path="freedom-report" element={<Perm perm="rpt_freedom"><FreedomPackageReport /></Perm>} />
              <Route path="staff-service-performance" element={<Perm perm="rpt_staff_service"><StaffServicePerformanceReport /></Perm>} />
              <Route path="service-revenue" element={<Perm perm="rpt_service_revenue"><ServiceRevenueReport /></Perm>} />
              <Route path="new-client-repeat" element={<Perm adminOnly><NewClientRepeatReport /></Perm>} />
              <Route path="new-vs-existing" element={<Perm perm="rpt_new_vs_existing"><NewVsExistingReport /></Perm>} />
              <Route path="retention" element={<Perm perm="rpt_retention"><RetentionReport /></Perm>} />
              <Route path="expense-report" element={<Perm perm="expense_report"><ExpenseReport /></Perm>} />
              <Route path="audit" element={<Perm perm="audit_log_view"><AuditLog /></Perm>} />
              <Route path="ai" element={<Perm perm="ai_assistant"><AIAssistant /></Perm>} />
              <Route path="settings" element={<Perm adminOnly><Settings /></Perm>} />
              <Route path="booking-site" element={<Perm perm="booking_site_manage"><BookingSite /></Perm>} />
              <Route path="website-images" element={<Perm perm="booking_site_manage"><WebsiteImages /></Perm>} />
              <Route path="managers" element={<Perm adminOnly><ManagerAccounts /></Perm>} />
              <Route path="menu" element={<Perm perm="service_menu"><ServiceMenu /></Perm>} />
              <Route path="enquiries" element={<Enquiries />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
